import { useLocation, useNavigate } from 'react-router';
import { routes } from '@/constants/Constants';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { usePermission } from '@/hooks/usePermission';
import { UserPermissions } from '@/constants/permissions.constant';
import useRenterInviteDrawerStore from '@/stores/useRenterInviteDrawerStore';
import { RENTER_INVITE_QUERY_PARAM } from '../constants';
import {
  buildInviteNavigationState,
  resolveInviteEntryPoint,
  type OpenInviteSource,
} from '../utils';

/**
 * FF ON + both Invite & App Tracker permissions:
 *   - If already on App Tracker → open drawer in-place
 *   - Otherwise → navigate to App Tracker with ?invite=open (auto-opens drawer)
 *
 * FF ON + Invite only (no App Tracker):
 *   - Open drawer on current page
 *
 * FF OFF:
 *   - Navigate to /renter-invite (existing full-page behavior)
 */
export const useRenterInviteAction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.MONARCH_460_NEW_RENTER_INVITE_IN_DRAWER,
  );

  const { openDrawer } = useRenterInviteDrawerStore();

  const { notSet: appTrackerNotSet, enabled: appTrackerEnabled } = usePermission(
    UserPermissions.Deal.CanAccessDealsDashboard,
  );
  const canAccessAppTracker = appTrackerNotSet || appTrackerEnabled;

  const isOnAppTracker = location.pathname === routes.dealsDashboard;

  const openInvite = (source?: OpenInviteSource) => {
    const inviteEntryPoint = resolveInviteEntryPoint(isOnAppTracker, source);
    const navigationState = buildInviteNavigationState(inviteEntryPoint, source);

    if (!isFeatureFlagEnabled) {
      navigate(routes.renterInvite, {
        state: navigationState,
      });
      return;
    }

    if (canAccessAppTracker) {
      if (isOnAppTracker) {
        openDrawer(inviteEntryPoint, navigationState.fromUrl);
      } else {
        navigate(`${routes.dealsDashboard}?${RENTER_INVITE_QUERY_PARAM}=true`, {
          state: navigationState,
        });
      }
    } else {
      openDrawer(inviteEntryPoint, navigationState.fromUrl);
    }
  };

  return { openInvite, isDrawerMode: isFeatureFlagEnabled };
};
